import styled, { keyframes } from 'styled-components';

// ============================================
// AiProgressTrail — 답을 기다리는 동안의 진행 줄
// ============================================
// 서버는 문장이 아니라 키만 준다({ key, state, domains }). 문장은 여기서 언어별로 만든다.
// 신 경로 훅(useAiChatServer)만 message.progress 를 채운다 — 레거시 /ai(useAiChat)는 이 값이 없어 아무것도 그리지 않는다.
//
// 말풍선 밖(헤더 아래)에 그린다 — 말풍선은 답이 오면 뜬다(2026-09-23 사용자 판단: 진행 문구는 말풍선 안에 넣지 않는다).
// 끝난 단계는 주황 체크 + 보조 글자색으로 쌓고, 진행 중 단계는 깜빡이는 점 + shimmer.
//   진행 중 단계가 없으면(0초~첫 기록 전, 조회 없는 턴) 로딩 문구(시간 사다리)가 그 자리에 온다 — 목록이 붙을 때 줄이 튀지 않게
//   사다리 문구도 같은 줄 모양으로 그린다. 진행 중 단계가 있으면 사다리 문구는 쓰지 않는다 — 단계 줄이 이미 진행을 말하고 있어
//   「시간이 조금 걸리고 있어요」 같은 시간 안내를 덧붙이면 정보 없는 줄이 된다(2026-09-23 사용자 판단).
// 답이 온 뒤에는 아무것도 그리지 않는다 — 「살펴본 과정」은 뺐다(같은 날 사용자 판단). 기록은 서버가 conversation_log.progress_json 에 남긴다.
// 톤: 아이콘은 RatingBar 와 같은 16px 인라인 SVG·테마 색, 글자는 14px·줄높이 22px.
// 모르는 단계 키는 그리지 않는다 — 서버는 형식만 검증하므로(Spring TurnProgressCodec) 새 단계가 먼저 와도 화면이 깨지지 않는다.

const LABELS = {
  ko: {
    triage: ['질문을 살펴보고 있어요', '질문을 살펴봤어요'],
    plan: ['조회 계획을 세우고 있어요', '조회 계획을 세웠어요'],
    query: [names => `${names} 기록을 조회하고 있어요`, names => `${names} 기록을 조회했어요`],
    storeRecords: '매장',
    separator: ', ',
    more: n => ` 외 ${n}곳`,
    domains: {
      sales: '매출', member: '회원', guest: '체험', membership: '회원권', reservation: '예약·출석', class: '수업',
      coach: '코치', point: '포인트', message: '발송', product: '상품', locker: '락커', notice: '공지', wod: 'WOD',
      partner: '제휴 체육관', store: '매장 설정',
    },
  },
  en: {
    triage: ['Reading your question', 'Read your question'],
    plan: ['Planning the lookup', 'Planned the lookup'],
    query: [names => `Looking up ${names} records`, names => `Looked up ${names} records`],
    storeRecords: 'store',
    separator: ', ',
    more: n => ` and ${n} more`,
    domains: {
      sales: 'sales', member: 'member', guest: 'trial', membership: 'membership', reservation: 'reservation', class: 'class',
      coach: 'coach', point: 'point', message: 'message', product: 'product', locker: 'locker', notice: 'notice', wod: 'WOD',
      partner: 'partner gym', store: 'store settings',
    },
  },
  ja: {
    triage: ['質問を確認しています', '質問を確認しました'],
    plan: ['照会の計画を立てています', '照会の計画を立てました'],
    query: [names => `${names}の記録を照会しています`, names => `${names}の記録を照会しました`],
    storeRecords: '店舗',
    separator: '、',
    more: n => ` ほか${n}件`,
    domains: {
      sales: '売上', member: '会員', guest: '体験', membership: '会員権', reservation: '予約・出席', class: 'クラス',
      coach: 'コーチ', point: 'ポイント', message: '送信', product: '商品', locker: 'ロッカー', notice: 'お知らせ', wod: 'WOD',
      partner: '提携ジム', store: '店舗設定',
    },
  },
};

const MAX_NAMED_DOMAINS = 3;

// 영역 키 → 이름 목록. 모르는 영역(data 등)만 있으면 「매장」 기록으로 뭉뚱그린다
function domainNames(domains, labels) {
  const names = (domains || []).map(domain => labels.domains[domain]).filter(Boolean);

  if (names.length === 0) return labels.storeRecords;

  const shown = names.slice(0, MAX_NAMED_DOMAINS).join(labels.separator);

  return names.length > MAX_NAMED_DOMAINS ? shown + labels.more(names.length - MAX_NAMED_DOMAINS) : shown;
}

// 기록 한 줄 → { text, running } (모르는 키·상태는 null)
function describe(item, labels) {
  const pair = labels[item?.key];

  if (!Array.isArray(pair) || (item.state !== 'run' && item.state !== 'done')) return null;

  const running = item.state === 'run';
  const template = pair[running ? 0 : 1];
  const text = typeof template === 'function' ? template(domainNames(item.domains, labels)) : template;

  return { key: item.key, text, running };
}

export default function AiProgressTrail({ progress, lang = 'ko', ladderText = '', theme }) {
  const labels = LABELS[lang] || LABELS.ko;
  const lines = (progress || []).map(item => describe(item, labels)).filter(Boolean);
  const done = lines.filter(line => !line.running);
  const running = lines.find(line => line.running);

  return (
    <TrailList>
      {done.map(line => (
        <TrailRow key={line.key}>
          <IconSlot><CheckIcon color={theme.primary} /></IconSlot>
          <RowText color={theme.subText}>{line.text}</RowText>
        </TrailRow>
      ))}
      <TrailRow>
        <IconSlot><PulseDot $color={theme.primary} /></IconSlot>
        <Shimmer color={theme.primary}>{running ? running.text : ladderText}</Shimmer>
      </TrailRow>
    </TrailList>
  );
}

// 완료 체크 (Material check) //
function CheckIcon({ color }) {
  return (
    <IconSvg viewBox='0 0 24 24' $color={color}>
      <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
    </IconSvg>
  );
}

// 답 말풍선의 글자 시작점(안쪽 10px)에 맞춘다 — 말풍선이 떴을 때 진행 줄과 답 글자가 같은 선에서 시작하게
const TrailList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  padding: 2px 10px 0;

  gap: 2px;
`;

const TrailRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  gap: 6px;
`;

const IconSlot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 16px;
  height: 16px;

  flex-shrink: 0;
`;

const IconSvg = styled.svg`
  width: 16px;
  height: 16px;

  fill: ${props => props.$color};

`;

const RowText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 22px;

  margin: 0;

  color: ${props => props.color};
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1); }
`;

// 진행 중 표시 — 로딩 shimmer 와 같은 주기로 숨쉬는 점 //
const PulseDot = styled.span`
  width: 7px;
  height: 7px;

  border-radius: 50%;

  background-color: ${props => props.$color};

  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const shimmerSweep = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

// AiChattingView ShimmerText 와 같은 표현 — 로딩 문구와 진행 중 단계가 같은 모양으로 보이게
const Shimmer = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 22px;

  white-space: pre-wrap;
  margin: 0;

  background: linear-gradient(
    90deg,
    ${props => props.color}44 0%,
    ${props => props.color}44 30%,
    ${props => props.color}A3 46%,
    ${props => props.color}C7 50%,
    ${props => props.color}A3 54%,
    ${props => props.color}44 70%,
    ${props => props.color}44 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmerSweep} 2.8s linear infinite;
`;
